import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useOutletContext, useParams, useSearchParams } from 'react-router-dom';
import { createSlug } from '../../../utils/helpers';
import { getCompanySlug } from '../../../client-experience/utils/companyAccess';
import { useStorefrontAuth } from '../../auth/context/StorefrontAuthContext';
import { getStorefrontAppointmentDetailPath } from '../../storefront-client-portal/utils/clientPortalPaths';
import { resolveCompanyAppointmentByParam } from '../../storefront-client-portal/utils/resolveCompanyAppointmentByParam';
import { getLandingPageBySlug } from '../../utils/custom-pages/pageConfig';
import { PageSkeleton } from '../../../components/loading/skeletons';
import {
  fetchStorefrontServices,
  findCachedStorefrontServiceBySlug,
  getCachedStorefrontServices,
} from '../../storefront-booking/storefrontServicesCache';
import ServiceDetailsPage from '../ServiceDetailsPage';
import CustomPage from '../CustomPage';
import CustomerBookingPage from '../CustomerBookingPage';
import '../../styles/LandingPage.css';

/**
 * Single-segment storefront URLs: /book/:companyUrl/:storefrontSlug
 * Resolves to a service (by name slug), then a custom landing page, then 404.
 * Booking flow: same path with ?book=1 (hides legacy /book-appointment/).
 */
const looksLikeAppointmentRef = (slug) => {
  if (!slug) return false;
  if (/^\d+$/.test(slug)) return true;
  if (slug.length >= 18 && /^[a-zA-Z0-9]+$/.test(slug)) return true;
  if (slug.length >= 4 && slug.length <= 12 && /^[a-zA-Z0-9]+$/.test(slug)) return true;
  return false;
};

const findServiceBySlug = (services, slug) =>
  services.find((service) => createSlug(service.name) === slug) || null;

const StorefrontSlugPage = () => {
  const navigate = useNavigate();
  const { companyUrl, storefrontSlug } = useParams();
  const [searchParams] = useSearchParams();
  const { companyData } = useOutletContext();
  const { hasPortalAccess, loading: authLoading, storefrontClient } = useStorefrontAuth();
  const [resolvedService, setResolvedService] = useState(null);
  const [asyncPhase, setAsyncPhase] = useState(null);

  const isBookingFlow = useMemo(() => {
    if (!searchParams.has('book')) return false;
    const raw = searchParams.get('book');
    if (raw === '0' || raw === 'false') return false;
    return true;
  }, [searchParams]);

  const customPage = useMemo(() => {
    if (!companyData?.id || !storefrontSlug) return null;
    const page = getLandingPageBySlug(
      companyData?.landingPageSettings,
      storefrontSlug,
      companyData
    );
    return page && page.isVisible !== false ? page : null;
  }, [companyData, storefrontSlug]);

  const cachedService = useMemo(
    () => findCachedStorefrontServiceBySlug(companyData?.id, storefrontSlug),
    [companyData?.id, storefrontSlug]
  );

  const syncPhase = useMemo(() => {
    if (!companyData?.id || !storefrontSlug) return 'none';
    if (cachedService) return 'service';

    const cachedServices = getCachedStorefrontServices(companyData.id);
    if (cachedServices) {
      if (customPage) return 'custom';
      return 'none';
    }

    return null;
  }, [companyData?.id, storefrontSlug, cachedService, customPage]);

  const phase = syncPhase ?? asyncPhase ?? 'loading';
  const prefetchedService = cachedService || resolvedService;

  useEffect(() => {
    setResolvedService(null);
    setAsyncPhase(null);

    if (!companyData?.id || !storefrontSlug) {
      setAsyncPhase('none');
      return;
    }

    if (syncPhase) {
      return;
    }

    let cancelled = false;

    const run = async () => {
      setAsyncPhase('loading');

      if (!authLoading && looksLikeAppointmentRef(storefrontSlug)) {
        try {
          const resolved = await resolveCompanyAppointmentByParam(companyData.id, storefrontSlug, {
            clientId: storefrontClient?.id || storefrontClient?.uid || '',
            clientEmail: storefrontClient?.email || '',
          });
          if (cancelled) return;
          if (resolved?.snap?.id && hasPortalAccess) {
            const companySlug = getCompanySlug(companyData) || companyUrl;
            navigate(getStorefrontAppointmentDetailPath(companySlug, resolved.snap.id), { replace: true });
            setAsyncPhase('appointment-redirect');
            return;
          }
        } catch (appointmentLookupError) {
          console.warn('[StorefrontSlugPage] appointment lookup failed', appointmentLookupError);
        }
      }

      try {
        const services = await fetchStorefrontServices(companyData.id);
        if (cancelled) return;

        const serviceMatch = findServiceBySlug(services, storefrontSlug);
        if (serviceMatch) {
          setResolvedService(serviceMatch);
          setAsyncPhase('service');
          return;
        }

        if (customPage) {
          setAsyncPhase('custom');
          return;
        }

        setAsyncPhase('none');
      } catch (e) {
        console.error('[StorefrontSlugPage]', e);
        if (!cancelled) {
          setAsyncPhase('none');
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [
    companyData?.id,
    storefrontSlug,
    authLoading,
    hasPortalAccess,
    companyUrl,
    navigate,
    storefrontClient,
    syncPhase,
    customPage,
  ]);

  if (phase === 'loading' || phase === 'appointment-redirect') {
    return (
      <div className="landing-page">
        <PageSkeleton padded variant="cards" label="Loading page" />
      </div>
    );
  }

  if (isBookingFlow) {
    if (prefetchedService) {
      return <CustomerBookingPage prefetchedService={prefetchedService} />;
    }
    if (phase === 'custom') {
      return <Navigate to={`/book/${companyUrl}/${storefrontSlug}`} replace />;
    }
    return (
      <div className="landing-page">
        <div className="error-container">
          <h2>Booking unavailable</h2>
          <p>That link doesn&apos;t match a bookable service.</p>
          <Link to={`/book/${companyUrl}`} className="back-link">
            Return to site
          </Link>
        </div>
      </div>
    );
  }

  if (phase === 'service' && prefetchedService) {
    return <ServiceDetailsPage prefetchedService={prefetchedService} />;
  }

  if (phase === 'custom') {
    return <CustomPage pageSlugOverride={storefrontSlug} />;
  }

  return (
    <div className="landing-page">
      <div className="error-container">
        <h2>Page not found</h2>
        <p>This link doesn&apos;t match a service or page for this site.</p>
        <Link to={`/book/${companyUrl}`} className="back-link">
          Return to site
        </Link>
      </div>
    </div>
  );
};

export default StorefrontSlugPage;
